import {
  createCategory,
  editCategory,
  removePostFromCategory,
  reorderCategoryItems,
} from "@/api/categories.api";
import { colorPalette } from "@/components/categories/categories-shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePostablesCommandSearch } from "@/hooks/usePostablesCommandSearch";
import { friendlyPostableTitle, friendlyPostableType } from "@/lib/utils";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import type { FeedPostables } from "@terrabase/db";
import type { Category } from "@zora/api/concerns/categories/categories.router";
import {
  type CategoryInput,
  type ReorderCategoryItemsInput,
  categoryZod,
} from "@zora/api/concerns/categories/categories.zod";
import type { Postable } from "@zora/api/concerns/postables/postables.router";
import { type PostableTypes, isRichPost } from "@zora/api/lib/post-helpers";
import debounce from "lodash/debounce";
import { ChevronLeft, MoreHorizontal, PlusCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { create } from "zustand";
import { PostablesCommandSeach } from "../command/postables-command-search";
import HorizontalMoveIcon from "../icons/horizontal-move-icon";
import { ConfirmModal } from "../ui/confirm-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Icons } from "../ui/icons";
import { SideSheet } from "../ui/side-sheet";
import { PostablePreviewSheet } from "./preview-sheet/postable-preview-sheet";
import { isBasicPost, isWebLink } from "./preview-sheet/preview-canvas";

type CategoryFormProps =
  | {
      isEdit: true;
      initialValues: Category;
    }
  | {
      isEdit: false;
      initialValues?: Category;
    };

function supportsOptions(
  item: FeedPostables,
  categoryDisplayType: string,
): boolean {
  return (
    categoryDisplayType === "grid" ||
    categoryDisplayType === "horizontal" ||
    categoryDisplayType === "horizontal-squares" ||
    isBasicPost(item) ||
    isWebLink(item)
  );
}

const usePostablesPreviewSheet = create<{
  postable: {
    id: string;
    postableId: string;
    postableType: PostableTypes;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    postable: any;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    json?: Record<string, any>;
    order: number;
  } | null;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  setPostable: (postable: any | null) => void;
}>((set) => ({
  postable: null,
  setPostable: (postable) => set({ postable }),
}));

function SortableRow({
  item,
  navigate,
  setRemovePostId,
  categoryDisplayType,
}: {
  // biome-ignore lint: @typescript-eslint/no-explicit-any
  item: any;
  // biome-ignore lint: @typescript-eslint/no-explicit-any
  navigate: any;
  setRemovePostId: (id: string | null) => void;
  categoryDisplayType: string;
}) {
  const setPreviewPostable = usePostablesPreviewSheet(
    (state) => state.setPostable,
  );
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: transform ? `translate3d(0px, ${transform.y}px, 0)` : undefined,
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} key={item.id}>
      <TableCell className="cursor-move" {...attributes} {...listeners}>
        <HorizontalMoveIcon className="handle h-4 w-4" />
      </TableCell>
      <TableCell>{friendlyPostableType(item.postableType)}</TableCell>
      <TableCell>{friendlyPostableTitle(item.postable)}</TableCell>
      <TableCell>{item.order + 1}</TableCell>
      {supportsOptions(item, categoryDisplayType) ? (
        <TableCell>
          <Button
            title="options"
            variant="secondary"
            onClick={() => {
              setPreviewPostable(item);
            }}
          >
            Options
          </Button>
        </TableCell>
      ) : null}
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button aria-haspopup="true" size="icon" variant="ghost">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            {item.id.includes("fake-") ? null : (
              <DropdownMenuItem
                onClick={() => {
                  let route = "";
                  switch (item.postableType) {
                    case "webLink":
                      route = `/web-links/${item.postableId}`;
                      break;
                    case "basicPost":
                      route = `/posts/${item.postableId}`;
                      break;
                    case "event":
                      route = `calendar/default/events/${item.postableId}`;
                      break;
                    case "contactCard":
                      route = `/contact-cards/${item.postableId}`;
                      break;
                    case "video":
                      route = `/videos/${item.postableId}`;
                      break;
                    case "place":
                      route = `/places/${item.postableId}`;
                      break;
                    case "nativeScreen":
                      route = `/native-screens/${item.postableId}`;
                      break;
                    default:
                      return "";
                  }
                  navigate({ to: route });
                }}
              >
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                setRemovePostId(item.id);
              }}
            >
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

export function CategoryForm({ isEdit, initialValues }: CategoryFormProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const [removePostId, setRemovePostId] = useState<string | null>(null);
  const setOpen = usePostablesCommandSearch((state) => state.setIsOpen);
  const [postables, setPostables] = useState(initialValues?.postables || []);
  const categoryId = initialValues?.id;
  const queryClient = useQueryClient();

  const previewPostable = usePostablesPreviewSheet((state) => state.postable);
  const setPreviewPostable = usePostablesPreviewSheet(
    (state) => state.setPostable,
  );

  const form = useForm<CategoryInput>({
    resolver: zodResolver(categoryZod),
    defaultValues: {
      title: initialValues?.title || "",
      // biome-ignore lint: @typescript-eslint/no-explicit-any
      displayType: (initialValues?.displayType as any) || "horizontal",
      backgroundColor: initialValues?.backgroundColor || "",
      postables: [],
    },
  });

  const { mutate: createCategoryMutation, isPending: isCreatingCategory } =
    useMutation({
      mutationFn: (category: CategoryInput) => createCategory(category),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["categories"] });
        navigate({ to: "/app/feed" });
      },
    });

  const { mutate: updateCategoryMutation, isPending: isUpdatingCategory } =
    useMutation({
      mutationFn: (category: CategoryInput & { id: string }) =>
        editCategory(category),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["categories"] });
        if (categoryId)
          queryClient.invalidateQueries({ queryKey: ["category", categoryId] });
        navigate({ to: "/app/feed" });
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const isSubmitting = isCreatingCategory || isUpdatingCategory;

  const { mutateAsync, isPending: deleteLoading } = useMutation({
    mutationFn: ({
      categoryId,
      postId,
    }: {
      categoryId: string;
      postId: string;
    }) => removePostFromCategory(categoryId, postId),
  });

  const { mutateAsync: reorderItems } = useMutation({
    mutationFn: (items: ReorderCategoryItemsInput & { categoryId: string }) =>
      reorderCategoryItems(items),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const debouncedUpdateOrder = debounce((categoryId, items) => {
    reorderItems({ categoryId, items }).catch((e) => {
      console.error(e);
    });
  }, 2500);

  const handleDragEnd = (event: {
    active: { id: number | string };
    over: { id: number | string } | null;
  }) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPostables((currentItems) => {
        const oldIndex = currentItems.findIndex(
          (item) => item.id === active.id,
        );
        const newIndex = currentItems.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(currentItems, oldIndex, newIndex);

        const updatedItems = newItems.map((item, index) => {
          return { ...item, order: index };
        });

        if (categoryId) debouncedUpdateOrder(categoryId, updatedItems);

        return updatedItems;
      });
    }
  };

  const onSubmit = (data: CategoryInput) => {
    if (isEdit && categoryId) {
      updateCategoryMutation({
        ...data,
        id: categoryId,
        postables: postables.map((item) => ({
          ...item,
          id: item.id.includes("fake-") ? undefined : item.id,
          deleted: false,
        })),
      });
    } else {
      createCategoryMutation({
        ...data,
        postables: postables.map((item) => ({
          ...item,
          id: item.id.includes("fake-") ? undefined : item.id,
          deleted: false,
        })),
      });
    }
  };

  const takesExtras = form.getValues("displayType") === "grid";

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const handlePostableUpdate = useCallback((updatedPostable: any) => {
    setPostables((currentItems) => {
      return currentItems.map((item) => {
        if (item.id === updatedPostable.id) {
          return {
            ...item,
            json: updatedPostable.json,
          };
        }
        return item;
      });
    });
  }, []);

  return (
    <div className="mx-auto grid max-w-[59rem] flex-1 auto-rows-max gap-4 mt-4">
      <PostablesCommandSeach
        filters={{
          types: [
            "basicPost",
            "event",
            "contactCard",
            "video",
            "webLink",
            "place",
            "nativeScreen",
          ],
        }}
        onItemSelect={(item: Postable) => {
          const itemToPostable = {
            id: `fake-${postables.length + 1}`,
            ...item,
            organizationId: initialValues?.organizationId || "",
            categoryId: initialValues?.id || "",
            order: postables.length,
            json: {},
          };
          setPostables((currentItems) => {
            return [...currentItems, itemToPostable];
          });
          setOpen(false);
        }}
        existingIds={postables.map((p) => p.postableId)}
      />
      <ConfirmModal
        open={Boolean(removePostId)}
        onConfirm={() => {
          const fake = removePostId?.includes("fake-");
          if (removePostId && initialValues?.id && !fake) {
            mutateAsync({ categoryId: initialValues?.id, postId: removePostId })
              .then(() => {
                setPostables((currentItems) => {
                  return currentItems.filter(
                    (item) => item.id !== removePostId,
                  );
                });
              })
              .catch(() => {});
          } else if (fake) {
            setPostables((currentItems) => {
              return currentItems.filter((item) => item.id !== removePostId);
            });
          }
          setRemovePostId(null);
        }}
        onDismiss={() => {
          setRemovePostId(null);
        }}
        title="Remove Post from Category"
        description="Are you sure you want to remove this post from the category? The post will not be deleted."
        isLoading={deleteLoading}
      />

      <Form {...form}>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              router.history.back();
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Back</span>
          </Button>
          <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
            {isEdit ? "Edit Category" : "New Category"}
          </h1>
          {isEdit ? (
            <Badge variant="outline" className="ml-auto sm:ml-0">
              {/* set the logos */}
              {initialValues?.displayType}
            </Badge>
          ) : null}
          <div className="hidden items-center gap-2 md:ml-auto md:flex">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                router.history.back();
              }}
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Icons.spinner className="h-4 w-4 mr-2" /> : null}
              Save Category
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
          <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
            <Card x-chunk="dashboard-07-chunk-0">
              <CardHeader>
                <CardTitle>Category</CardTitle>
                <CardDescription>
                  Categories allow you to organize your feed into sections. Each
                  section can have its own display type allowing you to create a
                  unique experience for your city.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid gap-3">
                    <FormField
                      control={form.control}
                      name={"title"}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input
                              autoFocus
                              type="text"
                              className="w-full"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card x-chunk="dashboard-07-chunk-1">
              <CardHeader>
                <CardTitle>Items</CardTitle>
                <CardDescription>
                  Add items to display in the feed under this category.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={postables}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead />
                          <TableHead>Type</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Order</TableHead>
                          {takesExtras ? <TableHead>Options</TableHead> : null}
                          <TableHead className="w-[100px]" />
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {postables.map((item) => (
                          <SortableRow
                            item={item}
                            key={item.id}
                            navigate={navigate}
                            setRemovePostId={setRemovePostId}
                            categoryDisplayType={form.getValues("displayType")}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </SortableContext>
                </DndContext>
              </CardContent>
              <CardFooter className="justify-center border-t p-4">
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1"
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Add Item
                </Button>
              </CardFooter>
            </Card>
          </div>
          <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
            <Card x-chunk="dashboard-07-chunk-3">
              <CardHeader>
                <CardTitle>Display Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid gap-3">
                    <FormField
                      control={form.control}
                      name="displayType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Types</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                id="displayType"
                                aria-label="Select display"
                              >
                                <SelectValue placeholder="Select display" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="stories">Stories</SelectItem>
                              <SelectItem value="vertical">Vertical</SelectItem>
                              <SelectItem value="horizontal">
                                Horizontal
                              </SelectItem>
                              <SelectItem value="grid">Grid</SelectItem>
                              <SelectItem value="horizontal-squares">
                                Horizontal Squares
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card x-chunk="dashboard-07-chunk-2">
              <CardHeader>
                <CardTitle>Display Properties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid gap-3">
                    <FormField
                      control={form.control}
                      name="backgroundColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="category">Color</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                id="category"
                                aria-label="Select color"
                              >
                                <SelectValue placeholder="Select color" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {colorPalette.map((color) => (
                                <SelectItem key={color.hex} value={color.hex}>
                                  <span
                                    style={{
                                      marginTop: "0.25em",
                                      display: "inline-block",
                                      width: "1em",
                                      height: "1em",
                                      borderRadius: "50%",
                                      backgroundColor: color.hex,
                                      marginRight: "0.5em",
                                      border: "1px solid #000",
                                    }}
                                  />
                                  {color.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid gap-3">
                    <FormField
                      control={form.control}
                      name="order"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="order">Order</FormLabel>
                          <FormControl>
                            <Input
                              id="order"
                              type="number"
                              className="w-full"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <SideSheet
          open={!!previewPostable}
          onClose={() => setPreviewPostable(null)}
          title="Web Builder Preview"
          width={420}
        >
          {previewPostable && (
            <PostablePreviewSheet
              postable={previewPostable}
              displayType={form.getValues("displayType")}
              onClose={() => setPreviewPostable(null)}
              onUpdate={handlePostableUpdate}
            />
          )}
        </SideSheet>
        <div className="flex items-center justify-center gap-2 md:hidden">
          <Button variant="outline" size="sm">
            Discard
          </Button>
          <Button
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Icons.spinner className="h-4 w-4 mr-2" /> : null}
            Save Category
          </Button>
        </div>
      </Form>
    </div>
  );
}
